import {
  useMutation,
  UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';
import {
  CrmIntegration,
  UpdateCrmIntegrationSettings,
} from '@tinct/core-api-models';
import { ApiError, tinctApi } from '../../api';

export type UpdateCrmIntegrationSettingsParams = {
  integrationId: string;
  settings: UpdateCrmIntegrationSettings;
};

export const updateCrmIntegrationSettings = async ({
  integrationId,
  settings,
}: UpdateCrmIntegrationSettingsParams): Promise<CrmIntegration> => {
  return await tinctApi.crmIntegrations.updateCrmIntegrationSettings(
    integrationId,
    settings,
  );
};

/**
 * Update an ads integration's settings (LinkedIn Ads: the ad accounts to
 * sync). The new scope applies to the next sync only — pair it with a "Sync
 * now" trigger when the user expects rows to change.
 */
export const useUpdateCrmIntegrationSettings = (
  options?: Omit<
    UseMutationOptions<
      CrmIntegration,
      ApiError,
      UpdateCrmIntegrationSettingsParams
    >,
    'mutationFn'
  >,
) => {
  const { onSuccess, ...restOptions } = options || {};

  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateCrmIntegrationSettings,
    onSuccess: (...args) => {
      const { integrationId } = args[1];
      queryClient.invalidateQueries({ queryKey: ['crm-integrations'] });
      queryClient.invalidateQueries({
        queryKey: ['ads-accounts', integrationId],
      });
      onSuccess?.(...args);
    },
    ...restOptions,
  });
};
